/* =====================================================================
   CLONE: homepage helpers — product tabs, scroll-snap carousel arrows, UGC video player.
   Loaded with defer from clone-* sections; guarded against double load.
   ===================================================================== */
(function () {
  if (window.CloneHome) return;

  class CloneTabs {
    constructor(root) {
      this.root = root;
      this.tabs = Array.from(root.querySelectorAll('[role="tab"]'));
      root.addEventListener('click', (e) => {
        const tab = e.target.closest('[role="tab"]');
        if (tab && this.tabs.includes(tab)) this.activate(tab);
      });
      root.addEventListener('keydown', (e) => this.onKeydown(e));
    }

    activate(tab, focus) {
      this.tabs.forEach((t) => {
        const on = t === tab;
        t.setAttribute('aria-selected', on ? 'true' : 'false');
        t.tabIndex = on ? 0 : -1;
        t.classList.toggle('is-active', on);
        const panel = document.getElementById(t.getAttribute('aria-controls'));
        if (panel) panel.hidden = !on;
      });
      if (focus) tab.focus();
    }

    onKeydown(e) {
      const i = this.tabs.indexOf(document.activeElement);
      if (i < 0) return;
      let next = null;
      if (e.key === 'ArrowRight') next = this.tabs[(i + 1) % this.tabs.length];
      if (e.key === 'ArrowLeft') next = this.tabs[(i - 1 + this.tabs.length) % this.tabs.length];
      if (e.key === 'Home') next = this.tabs[0];
      if (e.key === 'End') next = this.tabs[this.tabs.length - 1];
      if (next) {
        e.preventDefault();
        this.activate(next, true);
      }
    }
  }

  class CloneCarousel {
    constructor(root) {
      this.track = root.querySelector('[data-clone-track]');
      this.prev = root.querySelector('[data-clone-prev]');
      this.next = root.querySelector('[data-clone-next]');
      if (!this.track) return;
      const step = (dir) => this.track.scrollBy({ left: dir * this.track.clientWidth * 0.75 });
      if (this.prev) this.prev.addEventListener('click', () => step(-1));
      if (this.next) this.next.addEventListener('click', () => step(1));
      this.track.addEventListener('scroll', () => this.update(), { passive: true });
      window.addEventListener('resize', () => this.update(), { passive: true });
      this.update();
    }

    update() {
      const t = this.track;
      if (this.prev) this.prev.disabled = t.scrollLeft < 4;
      if (this.next) this.next.disabled = t.scrollLeft + t.clientWidth >= t.scrollWidth - 4;
    }
  }

  /* UGC videos: only posters load with the page; the file is requested on open */
  class CloneReviews {
    constructor(root) {
      this.items = Array.from(root.querySelectorAll('[data-ugc-src]'));
      this.dialog = root.querySelector('[data-ugc-player]');
      if (!this.items.length || !this.dialog || typeof this.dialog.showModal !== 'function') return;
      this.video = this.dialog.querySelector('[data-ugc-video]');
      this.product = this.dialog.querySelector('[data-ugc-player-product]');
      this.prev = this.dialog.querySelector('[data-ugc-step="-1"]');
      this.next = this.dialog.querySelector('[data-ugc-step="1"]');
      this.index = 0;

      root.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-ugc-src]');
        if (btn) this.open(this.items.indexOf(btn));
      });
      this.dialog.addEventListener('click', (e) => {
        const step = e.target.closest('[data-ugc-step]');
        if (step) this.show(this.index + Number(step.dataset.ugcStep));
        else if (e.target.closest('[data-ugc-close]') || e.target === this.dialog) this.dialog.close();
      });
      this.dialog.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') this.show(this.index + 1);
        if (e.key === 'ArrowLeft') this.show(this.index - 1);
      });
      this.dialog.addEventListener('close', () => this.onClose());
      this.video.addEventListener('ended', () => {
        if (this.index < this.items.length - 1) this.show(this.index + 1);
      });

      let x0 = null;
      this.dialog.addEventListener('touchstart', (e) => { x0 = e.touches[0].clientX; }, { passive: true });
      this.dialog.addEventListener('touchend', (e) => {
        if (x0 === null) return;
        const dx = e.changedTouches[0].clientX - x0;
        x0 = null;
        if (Math.abs(dx) > 50) this.show(this.index + (dx < 0 ? 1 : -1));
      }, { passive: true });
    }

    open(i) {
      if (i < 0) return;
      this.trigger = this.items[i];
      this.dialog.showModal();
      document.documentElement.classList.add('clone-player-open');
      this.show(i);
    }

    show(i) {
      if (i < 0 || i >= this.items.length) return;
      this.index = i;
      const btn = this.items[i];
      this.video.poster = btn.dataset.ugcPoster || '';
      this.video.src = btn.dataset.ugcSrc;
      const play = this.video.play();
      if (play && play.catch) play.catch(() => {});

      const chip = btn.parentElement.querySelector('[data-ugc-product]');
      this.product.replaceChildren();
      if (chip) this.product.appendChild(chip.cloneNode(true));

      this.prev.disabled = i === 0;
      this.next.disabled = i === this.items.length - 1;
    }

    onClose() {
      this.video.pause();
      this.video.removeAttribute('src');
      this.video.load();
      document.documentElement.classList.remove('clone-player-open');
      if (this.trigger) this.trigger.focus();
    }
  }

  function init(scope) {
    scope.querySelectorAll('[data-clone-reviews]').forEach((el) => {
      if (!el.cloneReviews) el.cloneReviews = new CloneReviews(el);
    });
    scope.querySelectorAll('[data-clone-tabs]').forEach((el) => {
      if (!el.cloneTabs) el.cloneTabs = new CloneTabs(el);
    });
    scope.querySelectorAll('[data-clone-carousel]').forEach((el) => {
      if (!el.cloneCarousel) el.cloneCarousel = new CloneCarousel(el);
    });
  }

  window.CloneHome = { init };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document));
  } else {
    init(document);
  }

  /* Theme editor: re-init re-rendered sections, show the selected tab */
  document.addEventListener('shopify:section:load', (e) => {
    init(e.target);
    if (typeof ProductForm !== 'undefined') {
      e.target.querySelectorAll('form[data-product-form]').forEach((form) => new ProductForm(form));
    }
  });

  document.addEventListener('shopify:block:select', (e) => {
    const tab = e.target.closest && e.target.closest('[role="tab"]');
    const root = tab && tab.closest('[data-clone-tabs]');
    if (root && root.cloneTabs) root.cloneTabs.activate(tab);
  });
})();
